import { useRef, useState } from "react";
import { round2 } from "./demandNoteCharges";

/**
 * Reviewer overrides for the Bahadura new-plot Demand Note charges.
 *
 * Each charge is worked out by a formula, and the reviewer may press the
 * pencil and type a different value. A typed value that differs from the
 * formula is an "override": it stays in the field after the reviewer presses
 * the lock, it is counted in the Total, and it is only replaced by the
 * formula value when the reviewer clears the field and locks it again.
 * (A reset icon that does the same in one click exists but is switched
 * off in the forms for now.)
 *
 * Shared by the Level 1, Level 2 and Level 3 Demand Note forms, which each
 * keep their own pencil/lock state and call into this module.
 */

/**
 * The charges that can be overridden. The key is the name the forms use for
 * the pencil/lock state; the value is the form field that holds the amount
 * (they differ only for Supervision Charges).
 */
export const OVERRIDABLE_CHARGE_FORM_FIELDS = {
  scrutinyCharges: "scrutinyCharges",
  particular2Total: "particular2Total",
  compoundingCharges: "compoundingCharges",
  supervisionTotal: "supervisionCharges",
  swmCharges: "swmCharges",
  administrativeCharges: "administrativeCharges",
  surveyMeasurementCharges: "surveyMeasurementCharges",
  infrastructureFee: "infrastructureFee",
  totalAmount: "totalAmount",
} as const;

export type TOverridableCharge = keyof typeof OVERRIDABLE_CHARGE_FORM_FIELDS;
export type TChargeOverrides = Record<TOverridableCharge, boolean>;

/** The formula value of every charge except the Total, which is a sum. */
export type TCalculatedCharges = Record<
  Exclude<TOverridableCharge, "totalAmount">,
  number
>;

const OVERRIDABLE_CHARGES = Object.keys(
  OVERRIDABLE_CHARGE_FORM_FIELDS
) as TOverridableCharge[];

export const NO_CHARGE_OVERRIDES: TChargeOverrides = {
  scrutinyCharges: false,
  particular2Total: false,
  compoundingCharges: false,
  supervisionTotal: false,
  swmCharges: false,
  administrativeCharges: false,
  surveyMeasurementCharges: false,
  infrastructureFee: false,
  totalAmount: false,
};

export function isOverridableCharge(field: string): field is TOverridableCharge {
  return Object.prototype.hasOwnProperty.call(
    OVERRIDABLE_CHARGE_FORM_FIELDS,
    field
  );
}

/**
 * True when the typed value is a real amount that differs from the formula
 * value. An empty or unreadable field is not an override, so clearing a
 * field and locking it brings the formula value back.
 */
export function isChargeOverridden(
  typedValue: unknown,
  calculatedValue: number
): boolean {
  if (typedValue === "" || typedValue === null || typedValue === undefined) {
    return false;
  }
  const typed = Number(typedValue);
  if (!Number.isFinite(typed)) return false;
  return round2(typed) !== round2(calculatedValue);
}

/**
 * Works out which charges of a saved Demand Note were overridden, by
 * comparing each saved amount with today's formula value.
 *
 * Nothing is treated as overridden when the application was edited after
 * the Demand Note was saved: the charges are then recalculated from the
 * edited application. A saved amount of 0 is not treated as an override
 * either, because the forms cannot tell a saved 0 from "never saved". The
 * Total is left out; it is always summed again from the charges.
 */
export function getSavedChargeOverrides(
  savedDemandNote: Record<string, any> | null | undefined,
  calculatedCharges: TCalculatedCharges
): TChargeOverrides {
  const overrides: TChargeOverrides = { ...NO_CHARGE_OVERRIDES };
  if (!savedDemandNote || savedDemandNote.applicationEditedAfterSave) {
    return overrides;
  }

  (Object.keys(calculatedCharges) as (keyof TCalculatedCharges)[]).forEach(
    (charge) => {
      const saved = savedDemandNote[OVERRIDABLE_CHARGE_FORM_FIELDS[charge]];
      if (!Number(saved)) return;
      overrides[charge] = isChargeOverridden(saved, calculatedCharges[charge]);
    }
  );
  return overrides;
}

/**
 * A charge keeps the value in its field, in place of the formula value,
 * while the reviewer has it unlocked or once it has been overridden.
 */
export function getChargesKeepingTypedValue(
  isEditable: Record<TOverridableCharge, boolean>,
  overrides: TChargeOverrides
): TChargeOverrides {
  const keepsTypedValue: TChargeOverrides = { ...NO_CHARGE_OVERRIDES };
  OVERRIDABLE_CHARGES.forEach((charge) => {
    keepsTypedValue[charge] = Boolean(isEditable[charge] || overrides[charge]);
  });
  return keepsTypedValue;
}

/**
 * Holds which charges the reviewer has overridden on one Demand Note form.
 *
 * The overrides are kept in a ref as well as in state: the form's
 * calculation needs the answer in the same pass in which it is worked out,
 * before the state update has been rendered.
 */
export function useNewPlotChargeOverrides() {
  const [overridden, setOverriddenState] =
    useState<TChargeOverrides>(NO_CHARGE_OVERRIDES);
  const overriddenRef = useRef<TChargeOverrides>(NO_CHARGE_OVERRIDES);
  const calculatedRef = useRef<Partial<Record<TOverridableCharge, number>>>({});
  const isSavedDemandNoteReadRef = useRef(false);

  const setOverridden = (next: TChargeOverrides) => {
    overriddenRef.current = next;
    setOverriddenState(next);
  };

  /**
   * Called by the form's calculation with the formula values it has just
   * worked out. Returns the overrides to apply in that same pass. The first
   * call also reads the saved Demand Note, so overrides saved earlier (by
   * this level or a previous one) are kept when the form is opened again.
   */
  const resolve = (
    savedDemandNote: Record<string, any> | null | undefined,
    calculatedCharges: TCalculatedCharges
  ): TChargeOverrides => {
    calculatedRef.current = { ...calculatedRef.current, ...calculatedCharges };
    if (!isSavedDemandNoteReadRef.current) {
      isSavedDemandNoteReadRef.current = true;
      setOverridden(getSavedChargeOverrides(savedDemandNote, calculatedCharges));
    }
    return overriddenRef.current;
  };

  const rememberCalculatedTotal = (calculatedTotal: number) => {
    calculatedRef.current = {
      ...calculatedRef.current,
      totalAmount: calculatedTotal,
    };
  };

  /**
   * Called when the reviewer presses the pencil/lock icon of any field.
   * Pressing the lock on a charge decides whether the typed value is an
   * override. Fields that are not charges, and charges whose formula value
   * is not known yet, are ignored.
   */
  const noteToggle = (
    field: string,
    wasEditable: boolean,
    getFormValue: (formField: string) => unknown
  ) => {
    if (!wasEditable || !isOverridableCharge(field)) return;
    const calculated = calculatedRef.current[field];
    if (calculated === undefined) return;

    setOverridden({
      ...overriddenRef.current,
      [field]: isChargeOverridden(
        getFormValue(OVERRIDABLE_CHARGE_FORM_FIELDS[field]),
        calculated
      ),
    });
  };

  /** Drops the override, so the calculation writes the formula value back. */
  const reset = (field: TOverridableCharge) => {
    setOverridden({ ...overriddenRef.current, [field]: false });
  };

  return { overridden, resolve, rememberCalculatedTotal, noteToggle, reset };
}
